// The Lender Hub's directory and each lender's routes, as `/service/v1/directory` and `/_hub/endpoints` would answer
// on UAT (plan v2 §3.4). Freo and mPokket have their kinds picked (the seed workflows use them); the others wait for
// someone to pick. MoneyView and SmartCoin are demo lenders whose UAT hosts are real lender servers: the portal
// never calls a lender, only the Hub, and the mock calls nothing at all.
import type { HubLenderRecord, HubRouteRecord } from '../state'
import { traffic } from './vendor-apis-exotel'

const SYNCED = '2026-10-24T08:00:00+05:30'

export const SEED_HUB_LENDERS: HubLenderRecord[] = [
  {
    slug: 'freo',
    name: 'Freo',
    synced_at: SYNCED,
    link_url: 'https://freo.money/apply?utm_source=firstfund&utm_medium=partner',
    connection: 'api',
  },
  {
    slug: 'mpokket',
    name: 'mPokket',
    synced_at: SYNCED,
    link_url: 'https://www.mpokket.in/apply?utm_source=firstfund&utm_campaign=ff_partner',
    connection: 'api',
  },
  { slug: 'lender-y', name: 'Lender Y (test lender)', synced_at: SYNCED, link_url: null, connection: 'api' },
  { slug: 'moneyview', name: 'MoneyView', synced_at: SYNCED, connection: 'api' },
  { slug: 'smartcoin', name: 'SmartCoin', synced_at: SYNCED, connection: 'api' },
]

/** PD64: what the Hub's caller list says of each API: its title, the applicant fields it takes, what its answer gives. */
const DETAILS: Record<string, Pick<HubRouteRecord, 'title' | 'takes' | 'gives'>> = {
  'hr-freo-dedupe': { title: 'Dedupe', takes: ['mobile', 'pan'], gives: [] },
  'hr-freo-create-lead': {
    title: 'Create lead',
    takes: ['mobile', 'name', 'pan', 'dob', 'incomeMonth', 'pincode'],
    gives: ['lender_ref', 'link'],
  },
  'hr-freo-status': { title: 'Lead status', takes: ['mobile'], gives: ['lender_ref', 'link'] },
  'hr-mpokket-check': { title: 'Customer check', takes: ['mobile'], gives: [] },
  'hr-mpokket-prefill': {
    title: 'Prefill',
    takes: ['mobile', 'name', 'pan', 'dob', 'pincode'],
    gives: ['lender_ref', 'link', 'data.utm_link'],
  },
  'hr-lender-y-dedupe': { title: 'Dedupe', takes: ['mobile', 'pan'], gives: [] },
  'hr-lender-y-lead': { title: 'Create lead', takes: ['mobile', 'name', 'incomeMonth'], gives: ['lender_ref'] },
  'hr-lender-y-offers': { title: 'Offers', takes: ['mobile'], gives: ['offers', 'link'] },
}

const route = (
  lender_slug: string,
  path: string,
  outcomes: HubRouteRecord['outcomes'],
  kind: HubRouteRecord['kind'] = null,
  busy = traffic(0, 800)
): HubRouteRecord => {
  const id = `hr-${lender_slug}-${path.slice(1).replace(/\//g, '-')}`
  return {
    id,
    lender_slug,
    method: 'POST',
    path,
    outcomes,
    kind,
    traffic: busy,
    title: null,
    takes: [],
    gives: outcomes.includes('redirect') ? ['link'] : [],
    ...DETAILS[id],
  }
}

export const SEED_HUB_ROUTES: HubRouteRecord[] = [
  route('freo', '/dedupe', ['ok', 'duplicate', 'error'], 'dedupe', traffic(25, 820, 0.01)),
  route('freo', '/create-lead', ['redirect', 'rejected', 'error'], 'prefill', traffic(18, 1400, 0.015)),
  route('freo', '/status', ['pending', 'approved', 'rejected', 'error'], 'status', traffic(4, 600)),
  route('mpokket', '/check', ['ok', 'duplicate', 'error'], 'dedupe', traffic(20, 700, 0.008)),
  // failing more than usual today: shows "errors rising"
  route('mpokket', '/prefill', ['redirect', 'error'], 'prefill', {
    ...traffic(25, 1600, 0.01),
    rising: 0.45,
  }),
  route('lender-y', '/dedupe', ['ok', 'duplicate', 'error'], 'dedupe', traffic(2, 300)),
  route('lender-y', '/lead', ['ok', 'rejected', 'error']),
  route('lender-y', '/offers', ['offer', 'rejected', 'error']),
  route('moneyview', '/dedupe', ['ok', 'duplicate', 'error']),
  route('moneyview', '/create-lead', ['ok', 'pending', 'error']),
  route('smartcoin', '/dedupe', ['ok', 'duplicate', 'error']),
  route('smartcoin', '/lead', ['redirect', 'rejected', 'error']),
]
